/*
 * The Villa OS logo: a Balinese roof over a setting sun and a pool, on a jungle-green tile.
 * Below ~40px the pool lines are dropped and the roof thickened so the mark stays legible.
 * Brand assets and the full guide live in public/brand/.
 */

type BrandMarkProps = {
  size?: number;
  /** Play the reveal animation once on mount (roof draws, sun rises, pool widens). */
  animated?: boolean;
  className?: string;
};

export function BrandMark({ size = 28, animated = false, className }: BrandMarkProps) {
  const compact = size < 40;
  const cls = (name: string) => (animated ? `bm-${name}` : undefined);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={["brand-mark", animated ? "brand-mark-animated" : "", className ?? ""].filter(Boolean).join(" ")}
      aria-hidden="true"
      focusable="false"
    >
      <rect className={cls("tile")} width="120" height="120" rx={compact ? 28 : 30} fill="#10302B" />
      {compact ? (
        <>
          <path d="M14 70 C36 60 49 42 60 18 C71 42 84 60 106 70" fill="none" stroke="#F3F5F1" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="60" cy="81" r="18" fill="#F2A23A" />
        </>
      ) : (
        <>
          <circle className={cls("sun")} cx="60" cy="72" r="14" fill="#F2A23A" />
          <path
            className={cls("roof")}
            pathLength={100}
            d="M14 68 C36 58 49 41 60 18 C71 41 84 58 106 68"
            fill="none"
            stroke="#F3F5F1"
            strokeWidth="9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path className={cls("w1")} d="M30 97 H90" stroke="#1F8A7A" strokeWidth="8" strokeLinecap="round" />
          <path className={cls("w2")} d="M45 108 H75" stroke="#1F8A7A" strokeWidth="8" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

/** Mark + "villaos" wordmark. The wordmark is decorative; the accessible name is the brand name. */
export function BrandLogo({ size = 28, animated = false, className }: BrandMarkProps) {
  return (
    <span className={["brand-logo", className ?? ""].filter(Boolean).join(" ")} role="img" aria-label="Villa OS">
      <BrandMark size={size} animated={animated} />
      <span className="brand-wordmark" aria-hidden="true">
        {"villa"}
        <span className="brand-wordmark-os">{"os"}</span>
      </span>
    </span>
  );
}
